import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'

import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import {
  getGetEmployersRenewalsRenewalConfigsIdEmployeesMetadataQueryKey as getRenewalEmployeesMetadataQueryKey,
  getGetEmployersRenewalsRenewalConfigsIdEmployeesQueryKey as getRenewalEmployeesQueryKey,
  useGetEmployersRenewalsRenewalConfigsIdEmployees as useGetRenewalEmployees,
  useGetEmployersRenewalsRenewalConfigsIdEmployeesMetadata as useGetRenewalEmployeesMetadata,
  usePatchEmployersRenewalsRenewalConfigsIdEmployeesMarkAllEligible as useMarkAllEmployeesEligible,
  usePutQuotingProspectsProspectIdCensusCensusIdEmployeesId as useUpdateCensusEmployee,
} from 'utils/web_api'
import type {
  EmployersRenewalsEmployee as RenewalEmployee,
  EmployersRenewalsEmployeeListMetadata as RenewalEmployeesMetadata,
  EmployersRenewalsPaginatedEmployeeList as RenewalEmployeesList,
  GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEmploymentTypesItem as EmploymentTypeFilter,
  GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEnrollmentStatusesItem as EnrollmentStatusFilter,
  PutQuotingProspectsProspectIdCensusCensusIdEmployeesId as UpdateRenewalProspectEmployeePayload,
} from 'utils/web_api.schemas'

type Props = {
  search?: string;
  enrollmentStatuses?: EnrollmentStatusFilter[];
  employmentTypes?: EmploymentTypeFilter[];
  excludeOffboardedEmployees?: boolean;
  pageIndex?: number;
  pageSize?: number;
  includeEmployeesWithMissingRequiredInfo?: boolean;
}

const DEFAULT_PAGE_SIZE = 10

const updateEligibleEmployeeIds = (old: RenewalEmployeesMetadata, id: string, eligible?: boolean) => {
  if (!old.eligible_employee_ids) return []
  if (eligible === undefined) return old.eligible_employee_ids

  const eligibleEmployeeIdsSet = new Set(old.eligible_employee_ids)

  if (eligible) {
    eligibleEmployeeIdsSet.add(id)
  } else {
    eligibleEmployeeIdsSet.delete(id)
  }

  return Array.from(eligibleEmployeeIdsSet)
}

export default function useRenewalEmployees ({
  search = '',
  enrollmentStatuses = [],
  employmentTypes = [],
  excludeOffboardedEmployees = true,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  includeEmployeesWithMissingRequiredInfo = true,
}: Props) {
  const queryClient = useQueryClient()

  const { renewalConfigId, employerRenewalConfig } = useEmployerRenewalConfig()

  const getEmployeesQueryKey = getRenewalEmployeesQueryKey(renewalConfigId, {
    'filter[name]': search,
    'filter[enrollment_statuses][]': enrollmentStatuses,
    'filter[employment_types][]': employmentTypes,
    'filter[exclude_offboarded_employees]': excludeOffboardedEmployees,
    'page[size]': pageSize,
    'page[number]': pageIndex + 1,
    'filter[include_employees_with_missing_required_information]': includeEmployeesWithMissingRequiredInfo,
  })
  const getMetadataQueryKey = getRenewalEmployeesMetadataQueryKey(renewalConfigId)

  const { data: metadata, isLoading: isLoadingMetadata } = useGetRenewalEmployeesMetadata(renewalConfigId, {
    query: {
      enabled: !!renewalConfigId,
      placeholderData: keepPreviousData,
    },
  })

  const { data: employees, isLoading: isLoadingEmployees, error } = useGetRenewalEmployees(
    renewalConfigId ?? '',
    {
      'filter[name]': search,
      'filter[enrollment_statuses][]': enrollmentStatuses,
      'filter[employment_types][]': employmentTypes,
      'filter[exclude_offboarded_employees]': excludeOffboardedEmployees,
      'page[size]': pageSize,
      'page[number]': pageIndex + 1,
      'filter[include_employees_with_missing_required_information]': includeEmployeesWithMissingRequiredInfo,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: !!renewalConfigId,
      },
    }
  )

  const markAllEligibleMutation = useMarkAllEmployeesEligible({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMetadataQueryKey })
      }
    }
  })

  const updateEmployeeMutation = useUpdateCensusEmployee({
    mutation: {
      onMutate: ({ prospectId: _prospectId, censusId: _censusId, id, data }) => {
        const previousEmployees = queryClient.getQueryData<RenewalEmployeesList>(getEmployeesQueryKey)
        const previousEmployee = previousEmployees?.data?.find((employee: RenewalEmployee) => employee.id === id)

        queryClient.setQueryData(getMetadataQueryKey, (old: RenewalEmployeesMetadata) => {
          return {
            ...old,
            eligible_employee_ids: updateEligibleEmployeeIds(old, id, data.eligible)
          }
        })

        // Since we could be caching multiple queries for the same renewal config, we need to update all of them
        const allEmployeesQueries = queryClient.getQueriesData({ queryKey: getRenewalEmployeesQueryKey(renewalConfigId) })
        allEmployeesQueries.forEach((query) => {
          queryClient.setQueryData(query[0], (old: RenewalEmployeesList) => {
            if (!old?.data) return old
            return {
              ...old,
              data: old.data.map((employee: RenewalEmployee) => {
                if (employee.id === id) {
                  return { ...employee, ...data }
                }
                return employee
              }),
            }
          })
        })

        return { previousEmployee }
      },
      onError: (error: any, _variables, context) => {
        if (context?.previousEmployee) {
          const previousEmployee = context.previousEmployee

          queryClient.setQueryData(getMetadataQueryKey, (old: RenewalEmployeesMetadata) => {
            return {
              ...old,
              eligible_employee_ids: updateEligibleEmployeeIds(old, previousEmployee.id, previousEmployee.eligible)
            }
          })

          const allEmployeesQueries = queryClient.getQueriesData({ queryKey: getRenewalEmployeesQueryKey(renewalConfigId) })
          allEmployeesQueries.forEach((query) => {
            queryClient.setQueryData(query[0], (old: RenewalEmployeesList) => {
              if (!old?.data) return old
              return {
                ...old,
                data: old.data.map((employee: RenewalEmployee) => {
                  if (employee.id === previousEmployee.id) {
                    return previousEmployee
                  }
                  return employee
                }),
              }
            })
          })
        }
        toast.error(`Failed to update employee${context?.previousEmployee?.name ? ` ${context.previousEmployee.name}` : ''}: ${error.message || error.errors?.join(', ') || 'Unknown error'}`, { duration: 10_000 })
      },
    },
  })

  const updateEmployee = useCallback(async (id: string, updates: UpdateRenewalProspectEmployeePayload) => {
    await updateEmployeeMutation.mutateAsync({
      prospectId: employerRenewalConfig?.prospect_id ?? '',
      censusId: employerRenewalConfig?.employee_census_id ?? '',
      id,
      data: {
        ...updates,
      },
    })
  }, [updateEmployeeMutation, employerRenewalConfig])

  const markAllEligible = useCallback(() => {
    markAllEligibleMutation.mutate({
      id: renewalConfigId,
    })
  }, [markAllEligibleMutation, renewalConfigId])

  const refetchEmployeeMetadata = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: getMetadataQueryKey })
    return queryClient.getQueryData<RenewalEmployeesMetadata>(getMetadataQueryKey)
  }, [queryClient, getMetadataQueryKey])

  const refetchEmployees = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: getRenewalEmployeesQueryKey(renewalConfigId) })
    return queryClient.getQueryData<RenewalEmployeesList>(getEmployeesQueryKey)?.data
  }, [queryClient, renewalConfigId, getEmployeesQueryKey])

  const eligibleEmployeeIds = useMemo(() => {
    return new Set(metadata?.eligible_employee_ids ?? [])
  }, [metadata])

  const enrolledEmployeeIds = useMemo(() => {
    return new Set(metadata?.enrolled_employee_ids ?? [])
  }, [metadata])

  return {
    employees: employees?.data ?? [],
    eligibleEmployeeIds,
    enrolledEmployeeIds,
    employeeIdsInvitedInCurrentYearWithMissingRequiredInfo: metadata?.employee_ids_invited_last_year_with_missing_info ?? [],
    numEmployeesWithMissingRequiredInfo: metadata?.num_employees_with_missing_required_info ?? 0,
    numOffboardedEmployees: metadata?.num_offboarded_employees ?? 0,
    pagination: employees?.pagination,
    isLoading: isLoadingEmployees || isLoadingMetadata,
    error,
    updateEmployee,
    updateEmployeeIsPending: updateEmployeeMutation.isPending,
    isMarkingAllEligible: markAllEligibleMutation.isPending,
    markAllEligible,
    refetchEmployees,
    refetchEmployeeMetadata,
  }
}
